<template>
  <section class="page" data-testid="page-evals">
    <header class="page-header">
      <div><h1>评测</h1><p>质量回归与人格策略护栏。</p></div>
      <div class="actions">
        <button class="button" :disabled="loading" @click="loadAll">{{ loading ? "刷新中..." : "刷新全部" }}</button>
        <button class="button primary" :disabled="loading" @click="() => loadReports()">刷新报告</button>
      </div>
    </header>
    <div class="metrics-grid">
      <MetricCard label="Suite" :value="suites.length" />
      <MetricCard label="历史报告" :value="reports.length" />
      <MetricCard label="满分报告" :value="perfectReportCount" />
      <MetricCard label="最近通过率" :value="latestPassRate" />
      <MetricCard label="当前匹配" :value="filteredReports.length" />
    </div>
    <p v-if="error" class="error-text">{{ error }}</p>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>
    <div class="panel">
      <h2>运行评测</h2>
      <div class="toolbar">
        <input v-model="suiteQuery" placeholder="搜索 suite" />
        <button class="button" :class="{ primary: suiteFilter === 'all' }" @click="suiteFilter = 'all'">全部</button>
        <button class="button" :class="{ primary: suiteFilter === 'small' }" @click="suiteFilter = 'small'">小套件</button>
        <button class="button" :class="{ primary: suiteFilter === 'large' }" @click="suiteFilter = 'large'">大套件</button>
      </div>
      <div class="suite-grid">
        <button
          v-for="item in filteredSuites"
          :key="item.id"
          class="suite-card"
          :class="{ active: selectedSuite === item.id }"
          type="button"
          @click="selectedSuite = item.id"
        >
          <span>{{ item.id }}</span>
          <strong>{{ item.name }}</strong>
          <small>{{ item.case_count }} cases</small>
          <p>{{ item.description }}</p>
        </button>
      </div>
      <div v-if="suites.length && !filteredSuites.length" class="empty">没有匹配的 suite。</div>
      <div class="toolbar run-toolbar">
        <select v-model="selectedSuite">
          <option v-for="item in suites" :key="item.id" :value="item.id">{{ item.id }}</option>
        </select>
        <button class="button primary" :disabled="running" data-testid="evals-run-submit" @click="runEval">{{ running ? "运行中..." : "运行" }}</button>
      </div>
    </div>
    <div v-if="runResult" class="metrics-grid">
      <MetricCard label="Suite" :value="runResult.suite || '-'" />
      <MetricCard label="Score" :value="String(runResult.summary?.score ?? '-')" />
      <MetricCard label="Passed" :value="String(runResult.summary?.passed || 0)" />
      <MetricCard label="Total" :value="String(runResult.summary?.total || 0)" />
      <MetricCard label="Pass Rate" :value="`${Math.round((runResult.summary?.pass_rate || 0) * 100)}%`" />
    </div>
    <div class="split">
      <div class="panel">
        <h2>历史报告</h2>
        <div class="toolbar">
          <input v-model="reportQuery" placeholder="搜索报告、suite、路径" />
          <button class="button" :class="{ primary: reportFilter === 'all' }" @click="reportFilter = 'all'">全部</button>
          <button class="button" :class="{ primary: reportFilter === 'perfect' }" @click="reportFilter = 'perfect'">满分</button>
          <button class="button" :class="{ primary: reportFilter === 'needs-work' }" @click="reportFilter = 'needs-work'">待修复</button>
        </div>
        <DataTable :columns="columns" :rows="filteredReports" row-key="path">
          <template #summary="{ row }">{{ formatSummary(row.summary) }}</template>
          <template #mtime="{ row }">{{ row.mtime ? new Date(row.mtime * 1000).toLocaleString("zh-CN") : "-" }}</template>
        </DataTable>
      </div>
      <div class="panel">
        <h2>本次结果</h2>
        <div v-if="runResult" class="run-summary">
          <strong>{{ runVerdict }}</strong>
          <span>{{ failedRunCases.length ? `${failedRunCases.length} 个失败用例需要关注` : "所有用例通过" }}</span>
        </div>
        <DataTable :columns="resultColumns" :rows="sortedRunResults" row-key="id">
          <template #passed="{ row }">
            <span class="result-pill" :class="row.passed ? 'passed' : 'failed'">{{ row.passed ? "通过" : "失败" }}</span>
          </template>
          <template #score="{ row }">{{ formatCaseScore(row.score) }}</template>
          <template #checks="{ row }">
            <span :class="{ 'failed-checks': failedChecks(row).length }">{{ formatFailedChecks(row) }}</span>
          </template>
        </DataTable>
        <details v-if="runResult" class="raw-result">
          <summary>原始响应</summary>
          <button class="button small" @click="copyResult">复制 JSON</button>
          <pre>{{ formattedResult }}</pre>
        </details>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { api, postJson } from "../api/client";
import { useClipboardResult } from "../composables/useClipboardResult";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import MetricCard from "../ui/MetricCard.vue";

type EvalSuite = {
  id: string;
  name: string;
  description: string;
  case_count: number;
};

type EvalSummary = {
  score?: number;
  passed?: number;
  total?: number;
  pass_rate?: number;
};

type EvalRunResult = {
  suite: string;
  summary: EvalSummary;
  results?: EvalCaseResult[];
  reports: Record<string, string>;
};

type EvalCaseResult = {
  id: string;
  score: number;
  passed: boolean;
  checks: Record<string, boolean>;
};

const toast = useToastStore();
const selectedSuite = ref("smoke");
const loading = ref(false);
const running = ref(false);
const error = ref("");
const actionResult = ref("");
const { copyText } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择结果");
const suiteQuery = ref("");
const suiteFilter = ref<"all" | "small" | "large">("all");
const reportQuery = ref("");
const reportFilter = ref<"all" | "perfect" | "needs-work">("all");
const suites = ref<EvalSuite[]>([]);
const reports = ref<any[]>([]);
const runResult = ref<EvalRunResult | null>(null);
const columns = [
  { key: "name", label: "报告" },
  { key: "suite", label: "Suite" },
  { key: "summary", label: "结果" },
  { key: "mtime", label: "时间" },
  { key: "path", label: "路径" }
];
const resultColumns = [
  { key: "id", label: "Case" },
  { key: "passed", label: "状态" },
  { key: "score", label: "分数" },
  { key: "checks", label: "失败检查" }
];

const formattedResult = computed(() => runResult.value ? JSON.stringify(runResult.value, null, 2) : "尚未运行");
const perfectReportCount = computed(() => reports.value.filter((report) => Number(report.summary?.pass_rate ?? 0) >= 1).length);
const latestPassRate = computed(() => reports.value[0]?.summary ? `${Math.round(Number(reports.value[0].summary.pass_rate || 0) * 100)}%` : "-");
const filteredSuites = computed(() => {
  const text = suiteQuery.value.trim().toLowerCase();
  return suites.value.filter((suite) => {
    if (suiteFilter.value === "small" && suite.case_count > 10) return false;
    if (suiteFilter.value === "large" && suite.case_count <= 10) return false;
    if (!text) return true;
    return `${suite.id} ${suite.name} ${suite.description}`.toLowerCase().includes(text);
  });
});
const filteredReports = computed(() => {
  const text = reportQuery.value.trim().toLowerCase();
  return reports.value.filter((report) => {
    const passRate = Number(report.summary?.pass_rate ?? 0);
    if (reportFilter.value === "perfect" && passRate < 1) return false;
    if (reportFilter.value === "needs-work" && passRate >= 1) return false;
    if (!text) return true;
    return `${report.name} ${report.suite} ${report.path}`.toLowerCase().includes(text);
  });
});
const sortedRunResults = computed(() => {
  return [...(runResult.value?.results || [])].sort((a, b) => {
    const status = Number(a.passed) - Number(b.passed);
    return status || a.id.localeCompare(b.id);
  });
});
const failedRunCases = computed(() => sortedRunResults.value.filter((item) => !item.passed));
const runVerdict = computed(() => {
  if (!runResult.value) return "";
  if (failedRunCases.value.length) return "评测未通过";
  return "评测通过";
});

async function loadSuites() {
  try {
    suites.value = (await api<{ suites: EvalSuite[] }>("/api/evals/suites")).suites || [];
    if (!suites.value.some((item) => item.id === selectedSuite.value)) {
      selectedSuite.value = suites.value[0]?.id || "smoke";
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : "评测 suite 加载失败";
  }
}

async function loadReports(setBusy = true) {
  if (setBusy) loading.value = true;
  error.value = "";
  try {
    reports.value = (await api<{ reports: any[] }>("/api/evals/reports")).reports || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : "评测报告加载失败";
  } finally {
    if (setBusy) loading.value = false;
  }
}

async function loadAll() {
  loading.value = true;
  error.value = "";
  try {
    await Promise.all([loadSuites(), loadReports(false)]);
  } finally {
    loading.value = false;
  }
}

function formatSummary(summary?: EvalSummary) {
  if (!summary) return "-";
  const passed = summary.passed ?? 0;
  const total = summary.total ?? 0;
  const passRate = Math.round((summary.pass_rate ?? 0) * 100);
  return `${passed}/${total} (${passRate}%)`;
}

function failedChecks(row: EvalCaseResult) {
  return Object.entries(row.checks || {})
    .filter(([, passed]) => !passed)
    .map(([key]) => key);
}

function formatFailedChecks(row: EvalCaseResult) {
  const failed = failedChecks(row);
  return failed.length ? failed.join(", ") : "全部通过";
}

function formatCaseScore(score: number) {
  return Number.isFinite(Number(score)) ? Number(score).toFixed(2) : "-";
}

async function runEval() {
  running.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    runResult.value = await postJson("/api/evals/run", { suite: selectedSuite.value || "smoke" });
    actionResult.value = failedRunCases.value.length ? "评测完成，存在失败用例" : "评测完成，全部通过";
    toast.push(actionResult.value);
    await loadReports();
  } catch (err) {
    error.value = err instanceof Error ? err.message : "评测运行失败";
    toast.push(error.value, "error");
  } finally {
    running.value = false;
  }
}

async function copyResult() {
  if (!formattedResult.value || formattedResult.value === "尚未运行") return;
  await copyText(formattedResult.value, "评测结果已复制");
}

onMounted(async () => {
  await loadAll();
});
</script>

<style scoped>
.suite-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.suite-card {
  min-height: 132px;
  display: grid;
  gap: 6px;
  align-content: start;
  text-align: left;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  color: var(--text);
  padding: 12px;
  cursor: pointer;
}

.suite-card.active {
  border-color: var(--primary);
  background: #16384a;
}

.suite-card span,
.suite-card small {
  color: var(--muted);
  font-size: 12px;
}

.suite-card strong {
  font-size: 16px;
}

.suite-card p {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
}

.run-toolbar {
  margin-bottom: 0;
}

.result-pill {
  display: inline-flex;
  width: 48px;
  justify-content: center;
  border-radius: 6px;
  padding: 3px 6px;
  font-size: 12px;
  border: 1px solid var(--border);
}

.result-pill.passed {
  color: var(--success);
  background: rgba(77, 185, 126, .08);
}

.result-pill.failed {
  color: var(--danger);
  background: rgba(226, 92, 92, .08);
}

.failed-checks {
  color: var(--danger);
}
.run-summary {
  display: grid;
  gap: 6px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px 12px;
  margin-bottom: 12px;
}
.run-summary strong {
  font-size: 16px;
}
.run-summary span {
  color: var(--muted);
}
.save-result {
  border: 1px solid #386f47;
  background: #142016;
  color: var(--success);
  border-radius: 8px;
  padding: 10px 12px;
}

.raw-result {
  margin-top: 12px;
  color: var(--muted);
}

.raw-result summary {
  cursor: pointer;
  margin-bottom: 8px;
}
</style>
